import {
  AdminCustomVDriveProviderSelect,
  AdminDatePickerInputModal,
  AdminDollarInput2,
  AdminInput,
  AdminInputPlaceholder,
  Box,
  Button,
  convert24HourTo12Hour,
  DeliveryAssignmentTimeEntity,
  displayCentsInDollars,
  Div,
  EditPenIcon,
  ErrorToast,
  format24hToDate,
  formatDateToTime24h,
  H3,
  Icon,
  Img,
  ModalPortal,
  P,
  PlusIcon,
  Radio,
  renderVDriveIcon,
  renderVDriveProviderName,
  SubTitleWithTooltipIcon,
  TrashIconRed,
  VDriveProvider,
} from '@vgrubs/components';
import { useCallback, useEffect, useState } from 'react';

export const DeliveryAssignmentTimes = ({
  times,
  setTimes,
  availableProviders,
}: {
  times: DeliveryAssignmentTimeEntity[];
  setTimes: (times: DeliveryAssignmentTimeEntity[]) => void;
  availableProviders: VDriveProvider[];
}) => {
  const [addTimeModal, setAddTimeModal] = useState<{
    edit: DeliveryAssignmentTimeEntity | null;
    index: number;
  } | null>(null);

  return (
    <>
      <Div $flex $alignCenter $between $mb="12" $mt="12" $mbSM="0" $mtSM="0">
        <SubTitleWithTooltipIcon
          mb="0"
          text={'Delivery Assignment Times'}
          tooltip={`
                This section lets you set delivery assignment rules based on order amount, time range, preparation time, and delivery provider.
              `}
        />
        <Icon
          $pointer
          onClick={() => setAddTimeModal({ edit: null, index: 0 })}
        >
          <PlusIcon />
        </Icon>
      </Div>
      {times.length > 0 && (
        <Div $flex $column $gap="8px">
          {times.map((item, i) => {
            return (
              <Box key={i} $flex $between $alignCenter $box>
                <Div $flex $column $gap="4px">
                  {item.amount !== null ? (
                    <P $mb="0"> {displayCentsInDollars(item.amount ?? 0)}</P>
                  ) : null}
                  {item.startTime && item.endTime ? (
                    <P $mb="0">
                      {convert24HourTo12Hour(item.startTime)} -{' '}
                      {convert24HourTo12Hour(item.endTime)}
                    </P>
                  ) : null}
                  {item.minutes !== null ? (
                    <P $mb="0">
                      {' '}
                      {item.minutes === 0 ? 'ASAP' : `${item.minutes} minutes`}
                    </P>
                  ) : null}
                  {item.provider !== null && item.provider !== undefined && (
                    <Div $flex $alignCenter $gap="8px">
                      <Img
                        src={renderVDriveIcon(item.provider ?? null)}
                        alt={item.provider ?? ''}
                        width={20}
                        height={20}
                      />
                      <P $mb="0">{renderVDriveProviderName(item.provider)}</P>
                    </Div>
                  )}
                </Div>
                <Div $flex $between $alignCenter $gap="16px" $gapSM="8px">
                  <Icon
                    $shadow
                    $pointer
                    onClick={() => setAddTimeModal({ edit: item, index: i })}
                  >
                    <EditPenIcon />
                  </Icon>
                  <Icon
                    $pointer
                    onClick={() => setTimes(times.filter((_, j) => j !== i))}
                  >
                    <TrashIconRed />
                  </Icon>
                </Div>
              </Box>
            );
          })}
        </Div>
      )}
      <AddDeliveryAssignmentTimeModal
        modalIsOpen={addTimeModal}
        closeModal={() => setAddTimeModal(null)}
        setTimes={setTimes}
        times={times}
        availableProviders={availableProviders}
      />
    </>
  );
};
const AddDeliveryAssignmentTimeModal = ({
  modalIsOpen,
  closeModal,
  setTimes,
  times,
  availableProviders,
}: {
  modalIsOpen: {
    edit: DeliveryAssignmentTimeEntity | null;
    index: number;
  } | null;
  closeModal: () => void;
  setTimes: (times: DeliveryAssignmentTimeEntity[]) => void;
  times: DeliveryAssignmentTimeEntity[];
  availableProviders: VDriveProvider[];
}) => {
  const [amount, setAmount] = useState<number | null>(null);
  const [startTime, setStartTime] = useState<Date[]>([]);
  const [endTime, setEndTime] = useState<Date[]>([]);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [provider, setProvider] = useState<VDriveProvider | null>(null);
  const [minutesModal, setMinutesModal] = useState<boolean>(false);
  const handleChangeAsapAmount = useCallback((val) => {
    setAmount(Number(val));
  }, []);

  const handleAddEdit = useCallback(() => {
    // the backend requires `minutes` on every time, the rest stay optional
    if (minutes === null || minutes === undefined) {
      ErrorToast('Please set the Preparation Time.');
      return;
    }

    if (modalIsOpen && typeof modalIsOpen === 'object' && modalIsOpen.edit) {
      setTimes(
        times.map((item, index) => {
          if (index === modalIsOpen.index) {
            return {
              ...item,
              amount,
              startTime:
                startTime.length > 0 ? formatDateToTime24h(startTime[0]) : null,
              endTime:
                endTime.length > 0 ? formatDateToTime24h(endTime[0]) : null,
              minutes: minutes ?? null,
              provider: provider ?? undefined,
            };
          }
          return item;
        })
      );
    } else {
      setTimes([
        ...times,
        {
          amount,
          startTime:
            startTime.length > 0 ? formatDateToTime24h(startTime[0]) : null,
          endTime: endTime.length > 0 ? formatDateToTime24h(endTime[0]) : null,
          minutes,
          provider: provider ?? undefined,
        },
      ]);
    }
    closeModal();
  }, [
    amount,
    startTime,
    endTime,
    setTimes,
    closeModal,
    modalIsOpen,
    minutes,
    provider,
    times,
  ]);

  useEffect(() => {
    if (modalIsOpen && typeof modalIsOpen === 'object' && modalIsOpen.edit) {
      setAmount(modalIsOpen.edit.amount ?? null);
      setStartTime(
        modalIsOpen.edit.startTime
          ? [format24hToDate(modalIsOpen.edit.startTime)]
          : []
      );
      setEndTime(
        modalIsOpen.edit.endTime
          ? [format24hToDate(modalIsOpen.edit.endTime)]
          : []
      );
      setMinutes(modalIsOpen.edit.minutes ?? null);
      setProvider(modalIsOpen.edit.provider ?? null);
    } else {
      setAmount(null);
      setStartTime([]);
      setEndTime([]);
      setMinutes(null);
      setProvider(null);
    }
  }, [modalIsOpen]);

  const renderMinutes = () => {
    if (minutes === null) return 'Enter here';
    if (minutes === 0) {
      return 'ASAP';
    }
    return `${minutes} min`;
  };

  return (
    <>
      <ModalPortal
        isOpen={!!modalIsOpen}
        closeModal={closeModal}
        overflow="visible"
      >
        <AdminDollarInput2
          label="Under Order Amount"
          onValueChange={handleChangeAsapAmount}
          value={amount ?? 0}
          $textCenter
          tooltip="Enter the subtotal order amount threshold up to which this order-level logic should apply."
          tooltipWidth="320px"
          onClickClear={
            amount === null
              ? undefined
              : (e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  setAmount(null);
                }
          }
        />
        <P $mt="24" $mb="16" $bold $center>
          Select Hours From - To
        </P>
        <Div $flex $gap="24px">
          <AdminDatePickerInputModal
            format="hh:mm a"
            options={{
              enableTime: true,
              dateFormat: 'Y-m-d H:i',
              noCalendar: true,
            }}
            placeholder="From"
            $clearable
            $textCenter
            onChange={setStartTime}
            value={startTime}
          />
          <AdminDatePickerInputModal
            format="hh:mm a"
            options={{
              enableTime: true,
              dateFormat: 'Y-m-d H:i',
              noCalendar: true,
            }}
            $clearable
            placeholder="To"
            $textCenter
            onChange={setEndTime}
            value={endTime}
          />
        </Div>
        <Div $mt="24">
          <AdminInputPlaceholder
            value={renderMinutes()}
            $textCenter
            label="Preparation Time"
            tooltip="Set the preparation time used when assigning the selected delivery provider. This helps ensure dispatch timing matches how long your team usually needs to prepare the order."
            // placeholder={'Enter here'}
            tooltipWidth="300px"
            onClick={() => setMinutesModal(true)}
          />
        </Div>
        <Div $mt="24">
          <AdminCustomVDriveProviderSelect
            value={provider}
            onChange={(value) => {
              setProvider(value as VDriveProvider);
            }}
            label="Delivery Provider"
            placeholder="Select Providers"
            $textCenter
            $shadowDesign={false}
            providerArray={availableProviders}
            $clearable
          />
        </Div>

        <Button $selected $w="100%" $mt="32" onClick={() => handleAddEdit()}>
          Confirm
        </Button>
        <Button $selected $gray $w="100%" $mt="16" onClick={() => closeModal()}>
          Cancel
        </Button>
      </ModalPortal>
      <MinutesModal
        minutes={minutes || null}
        minutesModal={minutesModal}
        setMinutesModal={setMinutesModal}
        setMinutes={setMinutes}
      />
    </>
  );
};

const MinutesModal = ({
  minutes,
  minutesModal,
  setMinutesModal,
  setMinutes,
}: {
  minutes: number | null;
  minutesModal: boolean;
  setMinutesModal: (v: boolean) => void;
  setMinutes: (minutes: number | null) => void;
}) => {
  const [minutesInput, setMinutesInput] = useState('');
  const [minutesType, setMinutesType] = useState<'minutes' | 'ASAP'>('minutes');

  function handleUpdateMinutes() {
    if (minutesType === 'minutes' && (!minutesInput || minutesInput === '0')) {
      ErrorToast('Please enter a preparation time in minutes');
      return;
    }

    if (minutesType === 'minutes') {
      setMinutes(Number(minutesInput));
    } else {
      setMinutes(0);
    }
    setMinutesModal(false);
  }

  useEffect(() => {
    if (minutes === 0) {
      setMinutesType('ASAP');
      setMinutesInput('');
    } else if (minutes !== null) {
      setMinutesType('minutes');
      setMinutesInput(minutes?.toString() || '');
    } else {
      setMinutesType('minutes');
      setMinutesInput('');
    }
  }, [minutes]);

  return (
    <ModalPortal
      isOpen={minutesModal}
      closeModal={() => {
        setMinutesModal(false);
      }}
      logo={false}
      width="300px"
      zIndex={131}
    >
      <H3>Preparation Time</H3>

      <Div $flex $alignCenter $between $height="50px">
        <Div
          $flex
          $alignCenter
          $gap="8px"
          onClick={() => setMinutesType('minutes')}
          $pointer
        >
          <Radio checked={minutesType === 'minutes'} />
          <P $m="0">Minutes</P>
        </Div>

        <Div $maxWidth="120px">
          <AdminInput
            onFocus={() => setMinutesType('minutes')}
            placeholder="Enter here"
            value={minutesInput}
            onChange={(e) => setMinutesInput(e.target.value)}
            $textCenter
          />
        </Div>
      </Div>

      {/* <Separator /> */}

      <Div
        $flex
        $alignCenter
        $gap="8px"
        onClick={() => setMinutesType('ASAP')}
        $pointer
        $height="50px"
      >
        <Radio checked={minutesType === 'ASAP'} />
        <P $m="0">ASAP</P>
      </Div>

      <Div $flex $gap="8px" $mt="24" $column>
        <Button $selected $w="100%" onClick={handleUpdateMinutes}>
          Save
        </Button>
        <Button
          $selected
          $w="100%"
          $gray
          onClick={() => {
            setMinutesModal(false);
          }}
        >
          Cancel
        </Button>
      </Div>
    </ModalPortal>
  );
};
